import React, { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Menu, X, Info, FileText, Stamp, FileCheck2, ClipboardList, HelpCircle, Building2, ChevronDown, Check } from "lucide-react";
import { BIZ, SERVICES, WHAT_TO_BRING, HOW_IT_WORKS, FAQ, REPUTATION } from "./scaleData";
import ConceptCTAPill from "@/components/ConceptCTAPill";
import LawInquiryForm from "@/components/LawInquiryForm";

const NAVY = "#101B33";
const STAMP = "#C2410C";
const SLATE = "#475569";
const LIGHT = "#F8FAFC";

const fadeUp = { initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-60px" }, transition: { duration: 0.5, ease: "easeOut" } };

const SERVICE_ICONS = [FileText, Stamp, FileCheck2, ClipboardList];

function FaqRow({ f }) {
  const [openRow, setOpenRow] = useState(false);
  return (
    <div className="border-b" style={{ borderColor: "#E2E8F0" }}>
      <button onClick={() => setOpenRow(!openRow)} className="w-full py-4 flex items-center justify-between text-left" data-testid={`scale-faq-toggle`}>
        <span className="font-semibold text-sm" style={{ color: NAVY }}>{f.q}</span>
        <ChevronDown size={18} style={{ color: STAMP, transform: openRow ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} />
      </button>
      {openRow && <p className="pb-4 text-sm" style={{ color: SLATE }}>{f.a}</p>}
    </div>
  );
}

export default function ScaleDocumentationSite() {
  const [open, setOpen] = useState(false);
  const links = [["Services", "#services"], ["What to Bring", "#bring"], ["FAQ", "#faq"], ["Contact", "#contact"]];

  return (
    <div id="top" className="min-h-screen" style={{ background: LIGHT, color: "#0F172A", fontFamily: "'Inter', sans-serif" }} data-testid="scale-concept-root">
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');`}</style>

      <div data-testid="law-disclaimer" className="bg-black text-white text-center text-xs py-2.5 px-4">
        <span className="inline-flex items-center gap-2"><Info size={13} style={{ color: STAMP }} />Concept website created by <a href="https://mysitecare.eu/ph" target="_blank" rel="noopener noreferrer" className="underline decoration-white/40 hover:decoration-white">MySiteCare</a> — not currently operated by or affiliated with {BIZ.name}.</span>
      </div>

      <header className="sticky top-0 z-40 shadow-sm" style={{ background: NAVY }}>
        <nav className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <a href="#top" data-testid="scale-logo" className="flex items-center gap-2 text-white font-extrabold tracking-tight">
            <Stamp size={20} style={{ color: STAMP }} /> SCALE
          </a>
          <div className="hidden lg:flex items-center gap-7">
            {links.map(([l, h]) => <a key={h} href={h} className="text-sm font-medium text-white/70 hover:text-white transition-colors">{l}</a>)}
          </div>
          <a href={`tel:${BIZ.phone}`} data-testid="scale-header-cta" className="hidden sm:inline-flex rounded-md px-4 py-2 text-sm font-semibold" style={{ background: STAMP, color: "white" }}>Call Now</a>
          <button className="lg:hidden text-white" onClick={() => setOpen(!open)} data-testid="scale-menu-toggle" aria-label="Menu">{open ? <X size={22} /> : <Menu size={22} />}</button>
        </nav>
        {open && <div className="lg:hidden px-5 pb-4 space-y-1" style={{ background: NAVY }}>{links.map(([l, h]) => <a key={h} href={h} onClick={() => setOpen(false)} className="block py-2.5 font-medium text-white/80">{l}</a>)}</div>}
      </header>

      {/* HERO — headline paired with a receipt-style process stub, not a photo */}
      <section className="py-14 sm:py-20" style={{ background: NAVY }} data-testid="scale-hero">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 grid lg:grid-cols-[1fr_0.8fr] gap-12 items-center">
          <motion.div {...fadeUp}>
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] rounded-full px-4 py-1.5" style={{ background: "rgba(194,65,12,0.18)", color: "#FDBA74" }}>
              <Building2 size={13} /> Makati CBD
            </span>
            <h1 className="mt-6 text-3xl sm:text-5xl font-extrabold text-white leading-[1.15]">Need a Document Notarized?</h1>
            <p className="mt-5 text-white/70 max-w-md">{BIZ.tagline}. Bring your document and valid ID to our office in Cityland Herrera Tower.</p>
            <div className="mt-8 flex flex-wrap gap-4">
              <a href={`tel:${BIZ.phone}`} data-testid="scale-hero-call" className="inline-flex items-center gap-2 rounded-md px-6 py-3.5 text-sm font-semibold" style={{ background: STAMP, color: "white" }}><Phone size={16} /> Call {BIZ.phoneDisplay}</a>
            </div>
          </motion.div>
          {/* Receipt-stub visual: the process itself, rendered as a document */}
          <motion.div {...fadeUp} className="bg-white rounded-lg shadow-2xl p-6 relative" style={{ borderTop: `4px dashed ${STAMP}` }}>
            <p className="text-[10px] uppercase tracking-[0.2em] font-bold" style={{ color: SLATE }}>Visit Checklist</p>
            <div className="mt-4 space-y-4">
              {HOW_IT_WORKS.map((s) => (
                <div key={s.n} className="flex items-start gap-3">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-bold text-white" style={{ background: NAVY }}>{s.n}</span>
                  <div>
                    <p className="text-sm font-semibold" style={{ color: NAVY }}>{s.title}</p>
                    <p className="text-xs" style={{ color: SLATE }}>{s.text}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-5 pt-4 border-t border-dashed flex items-center gap-2 text-xs font-semibold" style={{ borderColor: "#E2E8F0", color: STAMP }}>
              <Stamp size={14} /> Notarized on the spot
            </div>
          </motion.div>
        </div>
      </section>

      {/* SERVICES (list) + WHAT TO BRING (sidebar) — two-column body, not stacked card grids */}
      <section id="services" className="py-16">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 grid lg:grid-cols-[1.3fr_1fr] gap-12">
          <div>
            <motion.h2 {...fadeUp} className="text-2xl sm:text-3xl font-bold mb-8" style={{ color: NAVY }}>Documentation Services</motion.h2>
            <div className="divide-y" style={{ borderColor: "#E2E8F0" }}>
              {SERVICES.map((s, i) => {
                const Icon = SERVICE_ICONS[i];
                return (
                  <motion.div key={s.title} {...fadeUp} className="py-5 flex items-start gap-4">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md" style={{ background: "#FEE2D5" }}><Icon size={18} style={{ color: STAMP }} /></span>
                    <div>
                      <h3 className="font-semibold text-sm" style={{ color: NAVY }}>{s.title}</h3>
                      <p className="mt-1 text-xs" style={{ color: SLATE }}>{s.text}</p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
          <motion.div {...fadeUp} id="bring" className="bg-white rounded-lg p-6 sm:p-7 shadow-sm h-fit sticky top-24">
            <h3 className="font-bold text-lg flex items-center gap-2" style={{ color: NAVY }}><ClipboardList size={20} style={{ color: STAMP }} /> What to Bring</h3>
            <ul className="mt-4 space-y-3">
              {WHAT_TO_BRING.map((item) => (
                <li key={item} className="flex items-start gap-3 text-sm" style={{ color: SLATE }}>
                  <Check size={15} className="mt-0.5 shrink-0" style={{ color: STAMP }} />
                  {item}
                </li>
              ))}
            </ul>
            <div className="mt-6 pt-5 border-t text-center" style={{ borderColor: "#E2E8F0" }}>
              <p className="text-4xl font-extrabold" style={{ color: STAMP }}>{REPUTATION.rating}★</p>
              <p className="mt-1 text-xs" style={{ color: SLATE }}>across {REPUTATION.reviewCountLabel}</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ — accordion, not static cards */}
      <section id="faq" className="py-16" style={{ background: "#EEF2F7" }}>
        <div className="max-w-2xl mx-auto px-5 sm:px-8">
          <motion.h2 {...fadeUp} className="text-2xl sm:text-3xl font-bold text-center mb-10 flex items-center justify-center gap-2" style={{ color: NAVY }}><HelpCircle size={24} style={{ color: STAMP }} /> Frequently Asked Questions</motion.h2>
          <div className="bg-white rounded-lg px-6 shadow-sm">
            {FAQ.map((f) => <FaqRow key={f.q} f={f} />)}
          </div>
          <p className="mt-6 text-center text-xs" style={{ color: SLATE }}>{REPUTATION.note}</p>
        </div>
      </section>

      {/* CONTACT / OFFICE */}
      <section id="contact" className="py-16">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-10">
          <motion.div {...fadeUp}>
            <h2 className="text-2xl sm:text-3xl font-bold mb-6" style={{ color: NAVY }}>Office Location</h2>
            <ul className="space-y-4 text-sm mb-8">
              <li className="flex items-start gap-3"><MapPin size={18} style={{ color: STAMP }} className="mt-0.5 shrink-0" />{BIZ.address}</li>
              <li className="flex items-start gap-3"><Phone size={18} style={{ color: STAMP }} className="mt-0.5 shrink-0" /><a href={`tel:${BIZ.phone}`} data-testid="scale-phone-link" className="hover:underline">{BIZ.phoneDisplay}</a></li>
            </ul>
            <div className="rounded-lg overflow-hidden shadow-sm min-h-[240px]">
              <iframe title="SCALE Documentation Services map" src={BIZ.mapEmbed} className="w-full h-full min-h-[240px]" style={{ border: 0 }} loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
            </div>
          </motion.div>
          <motion.div {...fadeUp}>
            <LawInquiryForm
              testidPrefix="scale"
              businessName={BIZ.name}
              accent={STAMP}
              accentText="#FFFFFF"
              heading="Ask About Your Document"
              subheading="Share a few general details and we can confirm what's needed before your visit."
              areasOfLaw={["Notarization", "Contract Preparation", "Document Review", "Other / Not sure"]}
              consultationMethods={["Visit the office", "Phone call ahead", "No preference"]}
              phone={BIZ.phone}
              phoneDisplay={BIZ.phoneDisplay}
            />
          </motion.div>
        </div>
      </section>

      <footer className="py-8" style={{ background: NAVY }}>
        <div className="max-w-6xl mx-auto px-5 sm:px-8 flex flex-col sm:flex-row justify-between gap-3 text-xs text-white/50">
          <p className="font-bold text-white">SCALE Documentation Services</p>
          <p>This is an unsolicited concept website by MySiteCare. Not operated by or affiliated with {BIZ.name}. Confirm current details before visiting.</p>
        </div>
      </footer>

      <ConceptCTAPill slug="scale-documentation-makati-concept" />
    </div>
  );
}
